import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    orderBy,
    where,
    updateDoc,
    Timestamp,
    limit as firestoreLimit,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Member, EnrollmentStats } from '@/models/member.model';
import {
    auditView,
    auditListQuery,
    auditStatusChange,
    auditCreate,
    auditUpdate,
    auditDelete,
} from '@/utils/audit-integration';
import { AuditEntityType, PHIDataType, AccessReason } from '@/models/audit-log.model';

const MEMBERS_COLLECTION = 'members';
const ADMIN_MEMBERS_INDEX_COLLECTION = 'admin_index_members';

export class MemberService {
    /**
     * Get all members (uses admin index for performance)
     * WARNING: Contains PHI - ensure proper access control
     */
    static async getAllMembers(limitCount?: number): Promise<Member[]> {
        // Use admin index for efficient querying
        const q = query(
            collection(db, ADMIN_MEMBERS_INDEX_COLLECTION),
            orderBy('createdAt', 'desc'),
            ...(limitCount ? [firestoreLimit(limitCount)] : [])
        );
        
        const snapshot = await getDocs(q);
        const members: Member[] = [];
        
        // Fetch full member documents from index references
        for (const docSnap of snapshot.docs) {
            const indexData = docSnap.data();
            const memberId = indexData.memberId || docSnap.id;
            
            try {
                const memberDoc = await getDoc(doc(db, MEMBERS_COLLECTION, memberId));
                if (memberDoc.exists()) {
                    members.push(this.docToMember(memberDoc.id, memberDoc.data()));
                }
            } catch (error) {
                console.error(`Error fetching member ${memberId}:`, error);
            }
        }

        // Log PHI access
        auditListQuery(AuditEntityType.member, {
            queryName: 'all_members',
            resultCount: members.length,
            containsPHI: true,
            phiDataTypes: [PHIDataType.demographic, PHIDataType.contact],
            accessReason: AccessReason.operations,
        }).catch(console.error);

        return members;
    }

    /**
     * Get member by ID (logs PHI access)
     */
    static async getMemberById(id: string, logAccess: boolean = true): Promise<Member | null> {
        const docRef = doc(db, MEMBERS_COLLECTION, id);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
            return null;
        }
        
        const member = this.docToMember(docSnap.id, docSnap.data());
        
        // Log PHI access
        if (logAccess) {
            auditView(
                AuditEntityType.member,
                id,
                `${member.firstName} ${member.lastName}`,
                {
                    containsPHI: true,
                    phiDataTypes: [PHIDataType.demographic, PHIDataType.contact],
                    accessReason: AccessReason.operations,
                }
            ).catch(console.error);
        }
        
        return member;
    }

    /**
     * Get members by employer (uses admin index for performance)
     */
    static async getMembersByEmployer(employerId: string): Promise<Member[]> {
        // Use admin index for efficient querying
        const q = query(
            collection(db, ADMIN_MEMBERS_INDEX_COLLECTION),
            where('employerId', '==', employerId),
            orderBy('createdAt', 'desc')
        );
        
        const snapshot = await getDocs(q);
        const members: Member[] = [];
        
        // Fetch full member documents from index references
        for (const docSnap of snapshot.docs) {
            const indexData = docSnap.data();
            const memberId = indexData.memberId || docSnap.id;
            
            try {
                const memberDoc = await getDoc(doc(db, MEMBERS_COLLECTION, memberId));
                if (memberDoc.exists()) {
                    members.push(this.docToMember(memberDoc.id, memberDoc.data()));
                }
            } catch (error) {
                console.error(`Error fetching member ${memberId}:`, error);
            }
        }

        // Log PHI access
        auditListQuery(AuditEntityType.member, {
            queryName: 'members_by_employer',
            resultCount: members.length,
            filters: { employerId },
            containsPHI: true,
            phiDataTypes: [PHIDataType.demographic, PHIDataType.contact],
            accessReason: AccessReason.operations,
        }).catch(console.error);

        return members;
    }

    /**
     * Filter members by active status (uses admin index)
     */
    static async filterMembersByStatus(isActive: boolean): Promise<Member[]> {
        // Use admin index for efficient querying
        const q = query(
            collection(db, ADMIN_MEMBERS_INDEX_COLLECTION),
            where('isActive', '==', isActive),
            orderBy('createdAt', 'desc')
        );
        
        const snapshot = await getDocs(q);
        const members: Member[] = [];
        
        // Fetch full member documents from index references
        for (const docSnap of snapshot.docs) {
            const indexData = docSnap.data();
            const memberId = indexData.memberId || docSnap.id;
            
            try {
                const memberDoc = await getDoc(doc(db, MEMBERS_COLLECTION, memberId));
                if (memberDoc.exists()) {
                    members.push(this.docToMember(memberDoc.id, memberDoc.data()));
                }
            } catch (error) {
                console.error(`Error fetching member ${memberId}:`, error);
            }
        }

        // Log PHI access
        auditListQuery(AuditEntityType.member, {
            queryName: 'filter_members_by_status',
            resultCount: members.length,
            filters: { isActive },
            containsPHI: true,
            phiDataTypes: [PHIDataType.demographic, PHIDataType.contact],
            accessReason: AccessReason.operations,
        }).catch(console.error);

        return members;
    }

    /**
     * Search members by name or email
     */
    static async searchMembers(searchTerm: string): Promise<Member[]> {
        // For search, we need to query the main collection since admin index doesn't support text search
        // In production, consider using Algolia or similar for full-text search
        const allMembers = await this.getAllMembers(1000); // Limit for performance
        const lowerSearch = searchTerm.toLowerCase();
        
        const filtered = allMembers.filter(member =>
            member.email.toLowerCase().includes(lowerSearch) ||
            member.firstName?.toLowerCase().includes(lowerSearch) ||
            member.lastName?.toLowerCase().includes(lowerSearch) ||
            `${member.firstName} ${member.lastName}`.toLowerCase().includes(lowerSearch)
        );

        // Log PHI access
        auditListQuery(AuditEntityType.member, {
            queryName: 'search_members',
            resultCount: filtered.length,
            filters: { searchTerm },
            containsPHI: true,
            phiDataTypes: [PHIDataType.demographic, PHIDataType.contact],
            accessReason: AccessReason.operations,
        }).catch(console.error);

        return filtered;
    }

    /**
     * Update member (logs PHI modification)
     */
    static async updateMember(
        memberId: string,
        updates: Partial<Member>
    ): Promise<void> {
        const member = await this.getMemberById(memberId, false);
        if (!member) {
            throw new Error('Member not found');
        }

        const docRef = doc(db, MEMBERS_COLLECTION, memberId);
        const updateData: any = {
            lastUpdated: Timestamp.now(),
        };

        if (updates.firstName !== undefined) updateData.firstName = updates.firstName;
        if (updates.lastName !== undefined) updateData.lastName = updates.lastName;
        if (updates.email !== undefined) updateData.email = updates.email;
        if (updates.phoneNumber !== undefined) updateData.phoneNumber = updates.phoneNumber;
        if (updates.isActive !== undefined) updateData.isActive = updates.isActive;
        if (updates.employeeId !== undefined) updateData.employeeId = updates.employeeId;
        if (updates.bankAccountVerified !== undefined) {
            updateData.bankAccountVerified = updates.bankAccountVerified;
        }
        if (updates.bankAccountLast4 !== undefined) {
            updateData.bankAccountLast4 = updates.bankAccountLast4;
        }
        if (updates.bankAccountBankName !== undefined) {
            updateData.bankAccountBankName = updates.bankAccountBankName;
        }

        await updateDoc(docRef, updateData);

        // Log PHI modification
        auditUpdate(
            AuditEntityType.member,
            memberId,
            `${member.firstName} ${member.lastName}`,
            {
                containsPHI: true,
                phiDataTypes: [PHIDataType.demographic, PHIDataType.contact],
                fieldsChanged: Object.keys(updateData).filter(k => k !== 'lastUpdated'),
                beforeValues: member,
                afterValues: { ...member, ...updates },
            }
        ).catch(console.error);
    }

    /**
     * Update member status (logs status change)
     */
    static async updateMemberStatus(
        memberId: string,
        isActive: boolean
    ): Promise<void> {
        const member = await this.getMemberById(memberId, false);
        if (!member) {
            throw new Error('Member not found');
        }

        await this.updateMember(memberId, { isActive });

        // Log status change
        auditStatusChange(
            AuditEntityType.member,
            memberId,
            `${member.firstName} ${member.lastName}`,
            member.isActive ? 'active' : 'inactive',
            isActive ? 'active' : 'inactive'
        ).catch(console.error);
    }

    /**
     * Get enrollment statistics (uses admin index for performance)
     */
    static async getEnrollmentStats(): Promise<EnrollmentStats> {
        // Use admin index for efficient counting
        const activeMembers = await this.filterMembersByStatus(true);
        const allMembers = await this.getAllMembers(10000); // Reasonable limit

        const totalInvited = allMembers.length;
        const activeUsers = activeMembers.length;
        const bankConnected = allMembers.filter(m => m.bankAccountVerified).length;

        return {
            totalInvited,
            activeUsers,
            bankConnected,
            onboardingRate: totalInvited > 0 ? (activeUsers / totalInvited) * 100 : 0,
            bankConnectionRate: activeUsers > 0 ? (bankConnected / activeUsers) * 100 : 0,
        };
    }

    /**
     * Convert Firestore document to Member
     */
    private static docToMember(id: string, data: any): Member {
        return {
            id,
            userId: data.userId,
            employerId: data.employerId,
            employeeId: data.employeeId,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            phoneNumber: data.phoneNumber,
            isActive: data.isActive ?? false,
            enrollmentDate: data.enrollmentDate?.toDate() || new Date(),

            stripeCustomerId: data.stripeCustomerId,
            bankAccountVerified: data.bankAccountVerified ?? false,
            bankAccountLast4: data.bankAccountLast4,
            bankAccountBankName: data.bankAccountBankName,

            createdAt: data.createdAt?.toDate() || new Date(),
            lastUpdated: data.lastUpdated?.toDate() || new Date(),
        };
    }
}
